<script setup>
import { nextTick, ref } from 'vue';
import RichTextEditor from '@/js/core/components/parts/editor/RichTextEditor.vue';
import { useProfileStore } from '@/js/core/stores/profile-store';

const props = defineProps({
  bio: {
    type: String,
    default: '',
  },
});

const profileStore = useProfileStore();
const draftBio = ref('');
const isEditingBio = ref(false);
const isSavingBio = ref(false);
const bioError = ref('');
const bioFieldError = ref('');
const bioEditorRef = ref(null);

const handleBioEdit = async () => {
  draftBio.value = props.bio;
  bioError.value = '';
  bioFieldError.value = '';
  isEditingBio.value = true;
  await nextTick();
  bioEditorRef.value?.focus();
};

const cancelBioEdit = () => {
  draftBio.value = '';
  bioError.value = '';
  bioFieldError.value = '';
  isEditingBio.value = false;
};

const saveBioEdit = async () => {
  isSavingBio.value = true;
  bioError.value = '';
  bioFieldError.value = '';

  try {
    const content = bioEditorRef.value?.getContent() ?? draftBio.value;
    await profileStore.saveCurrentProfile({ bio: content });
    draftBio.value = '';
    isEditingBio.value = false;
  } catch (error) {
    bioFieldError.value = error.errors?.bio || '';
    bioError.value = bioFieldError.value
      ? ''
      : error.message || 'Failed to save your biography. Please try again.';
  } finally {
    isSavingBio.value = false;
  }
};
</script>

<template>
  <div class="profile-page_bio-container">
    <div v-if="!isEditingBio" class="profile-page_bio-content">
      <p v-if="!bio" class="profile-page_bio-placeholder">
        Share a bit about yourself. This content will be displayed on your profile page. If your
        profile visibility is public, other users can see this content when they click your name in
        any Tray you’ve joined or commented on.
      </p>
      <div v-else class="profile-page_bio-rendered" v-html="bio" />
      <div class="profile-page_bio-edit-button-container">
        <button class="profile-page_bio-edit-button" type="button" @click="handleBioEdit">
          Edit
        </button>
      </div>
    </div>

    <div v-else class="profile-page_bio-editing">
      <RichTextEditor
        ref="bioEditorRef"
        v-model="draftBio"
        placeholder="Share a bit about yourself. This content will be displayed on your profile page. If your profile visibility is public, other users can see this content when they click your name in any Tray you’ve joined or commented on."
        :show-subscript="true"
        :show-superscript="true"
        :show-h1="true"
        :show-h2="true"
        :show-blockquote="true"
        editor-class="profile-page_bio-editor"
        toolbar-class="profile-page_bio-toolbar"
        button-class="profile-page_bio-toolbar-btn"
      />
      <p v-if="bioFieldError" class="profile-page_contacts-helper is-error" role="alert">
        {{ bioFieldError }}
      </p>
      <p v-if="bioError" class="profile-page_contacts-helper is-error" role="alert">
        {{ bioError }}
      </p>
      <div class="profile-page_bio-actions">
        <button
          class="profile-page_bio-action-btn profile-page_bio-cancel"
          type="button"
          :disabled="isSavingBio"
          @click="cancelBioEdit"
        >
          Cancel
        </button>
        <button
          class="profile-page_bio-action-btn profile-page_bio-save"
          type="button"
          :disabled="isSavingBio"
          @click="saveBioEdit"
        >
          {{ isSavingBio ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
</template>
